import Leaflet, { LatLngBounds } from 'leaflet';
import { RawResult } from 'leaflet-geosearch/dist/providers/openStreetMapProvider.js';
import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, useMapEvents, ZoomControl } from 'react-leaflet';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import MapUserIcon from './MapUserIcon.tsx';

// Fix Leaflet import icons
// TODO move this to separate module
import L, { LatLng } from 'leaflet';

import { Flex, useAuthenticator } from "@aws-amplify/ui-react";
import CreateNewPlacePopup from "./CreateNewPlacePopup";

import I18nTextUtils from "../utils/i18n/I18nTextUtils.ts";
import PlaceDataUtils, { NewPlaceDefinition, PlaceDefinition } from "../utils/PlaceDataUtils.ts";
import ClientDeviceUtils from "../utils/client/ClientDeviceUtils.ts";
import './MapWindow.css';
import UserInfoPopup from "./UserInfoPopup.tsx";

import { AuthSession, fetchAuthSession, fetchUserAttributes } from "aws-amplify/auth";
import { SearchResult } from 'leaflet-geosearch/dist/providers/provider.js';
import LocationControls from "./location/LocationControls.tsx";
import LocationSearch from './location/LocationSearch.tsx';
import MyLocationMarker from "./location/MyLocationMarker.tsx";
import PlaceOverviewPopup from './PlaceOverviewPopup.tsx';
import { PlaceMarkerHoverClass } from './map/PlaceMarkerHoverClass.tsx';
import { PlaceMarkerNoHoverClass } from './map/PlaceMarkerNoHoverClass.tsx';
import { PlaceMarkerClusterGroup } from './map/PlaceMarkerClusterGroup.ts';
import { SummaryClusterMarker } from './map/SummaryClusterMarker.tsx';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

let DefaultIcon = L.icon({
    iconSize: [25, 41],
    iconAnchor: [13, 40],
    popupAnchor: [0, -40],
    iconUrl: 'https://unpkg.com/leaflet@1.4.0/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.4.0/dist/images/marker-shadow.png'
});

L.Marker.prototype.options.icon = DefaultIcon;

export interface PlaceDefinitionMapMember {
    key: string
    place: Leaflet.LatLng
    address?: string
    name: string
    description?: string
}

const INITIAL_MAP_ZOOM: number = 2;
const INITIAL_MAP_POSITION: LatLng = new LatLng(0, 0)
const DEFAULT_MAP_ZOOM: number = 10;
const LOCATION_UPDATE_TIMEOUT_MS: number = 100
// FlyTo does not result in map center in exact location - in default zoom it is ~ 50 meters
// TODO review this - need expected behavior when zoomed and moving with device locked on location
const FLY_TO_LOCATION_DISTANCE_METERS_TRESHOLD: number = 70

// Statuses
let positionLoading: boolean = true

export default function MapWindow() {

    const navigate = useNavigate();
    const { placeId } = useParams<{ placeId?: string }>();
    const location = useLocation();

    const { user } = useAuthenticator(context => [context.user]);

    const { authStatus } = useAuthenticator(context => [context.authStatus]);

    const [showCreateNewPlaceOverlay, setShowCreateNewPlaceOverlay] = useState(false);
    const [showUserInfoOverlay, setShowUserInfoOverlay] = useState<boolean>(false)

    const [newPlaceLatLen, setNewPlaceLatLen] = useState<LatLng | undefined>(undefined);
    const [newMarkerPositions, setNewMarkerPositions] = useState<PlaceDefinitionMapMember[]>([])
    const [places, setPlaces] = useState<PlaceDefinition[]>([])

    const [myLocation, setMyLocation] = useState<LatLng | undefined>(undefined);
    const [isMyLocationActive, setIsMyLocationActive] = useState<boolean>(true)

    const [isMapWindowVisible, setIsMapWindowVisible] = useState<boolean>(true);

    const [authSession, setAuthSession] = useState<AuthSession>()

    const [isPlaceOverviewVisible, setIsPlaceOverviewVisible] = useState(false);
    const [selectedPlaceDefinition, setSelectedPlaceDefinition] = useState<PlaceDefinition | null>(null);

    // Track if place is being opened from URL (vs clicked on map) to determine if map should center
    const isOpeningFromUrlRef = useRef<boolean>(false);
    // Track if URL update is from internal navigation (to avoid loops)
    const isInternalNavigationRef = useRef<boolean>(false);

    // const { tokens } = useTheme();

    const mapShareRef = useRef<Leaflet.Map | undefined>(undefined);
    const markerClusterGroupRef = useRef<PlaceMarkerClusterGroup | undefined>(undefined);
    const summaryClusterMarkersRef = useRef<SummaryClusterMarker[]>([]);
    const expandedPlacesMapRef = useRef<Record<string, boolean>>({});
    const placeRefreshTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const isUpdatingPlacesRef = useRef<boolean>(false); // Prevent concurrent updates
    const shouldZoomToDefaultOnLocationFoundRef = useRef<boolean>(false); // Track if we should zoom to 10 when location is found

    const fillAndSubscribePlacesList = async (bounds?: LatLngBounds) => {
        // Prevent concurrent executions
        if (isUpdatingPlacesRef.current) {
            console.log("Place update already in progress, skipping duplicate call");
            return;
        }

        try {
            if (!mapShareRef.current?.getBounds()) {
                // If map is not loaded yet, do not do anything
                return;
            }

            isUpdatingPlacesRef.current = true;
            const currentBounds = bounds || mapShareRef.current.getBounds();
            const currentZoom = mapShareRef.current.getZoom();

            // When zoom < 10, use optimized summary (count + categories only)
            if (currentZoom < 10) {
                console.log("Low zoom level (< 10), using summary mode for bounds:", currentBounds);
                
                // Clear individual place markers
                if (markerClusterGroupRef.current) {
                    markerClusterGroupRef.current.clearLayers();
                }
                setPlaces([]);

                // Remove existing summary markers synchronously before creating new ones
                if (mapShareRef.current) {
                    console.log(`Removing ${summaryClusterMarkersRef.current.length} existing summary markers`);
                    summaryClusterMarkersRef.current.forEach(marker => {
                        if (mapShareRef.current) {
                            mapShareRef.current.removeLayer(marker);
                        }
                    });
                    summaryClusterMarkersRef.current = [];
                }

                // Split bounds into 3x3 grid (9 cells)
                const north = currentBounds.getNorth();
                const south = currentBounds.getSouth();
                const east = currentBounds.getEast();
                const west = currentBounds.getWest();

                // Calculate lat and lng divisions for 3x3 grid
                const latStep = (north - south) / 3;
                const lngStep = (east - west) / 3;

                // Create 9 grid cells (3 rows x 3 columns)
                // Each cell is defined by southwest and northeast corners
                const gridCells: Array<{ name: string; bounds: LatLngBounds; center: LatLng }> = [];
                
                for (let row = 0; row < 3; row++) {
                    for (let col = 0; col < 3; col++) {
                        const cellSouth = south + row * latStep;
                        const cellNorth = south + (row + 1) * latStep;
                        const cellWest = west + col * lngStep;
                        const cellEast = west + (col + 1) * lngStep;
                        
                        const cellCenterLat = (cellSouth + cellNorth) / 2;
                        const cellCenterLng = (cellWest + cellEast) / 2;

                        gridCells.push({
                            name: `cell_${row}_${col}`,
                            bounds: new LatLngBounds(
                                new LatLng(cellSouth, cellWest),  // Southwest corner
                                new LatLng(cellNorth, cellEast)   // Northeast corner
                            ),
                            center: new LatLng(cellCenterLat, cellCenterLng)
                        });
                    }
                }

                // Get summary for each grid cell in parallel
                const gridCellSummaries = await Promise.all(
                    gridCells.map(gridCell => 
                        PlaceDataUtils.getPlacesSummaryForBounds(user, gridCell.bounds, 0.0)
                            .then(summary => ({ gridCell, summary }))
                    )
                );

                // Create and add summary markers for each grid cell
                if (mapShareRef.current) {
                    const newMarkers: SummaryClusterMarker[] = [];
                    gridCellSummaries.forEach(({ gridCell, summary }) => {
                        if (summary && summary.totalCount > 0) {
                            const summaryMarker = new SummaryClusterMarker(
                                summary.totalCount,
                                summary.isAtLimit,
                                summary.categories,
                                gridCell.center.lat,
                                gridCell.center.lng,
                                summary.calculatedCenterLat,
                                summary.calculatedCenterLng
                            );

                            summaryMarker.addTo(mapShareRef.current!);
                            newMarkers.push(summaryMarker);

                            // Add click handler to zoom in when count-only cluster is clicked
                            // Use calculated center (mean of returned places) for zoom target
                            // Also deactivate location tracking when clicked (same as regular clusters)
                            summaryMarker.on('click', () => {
                                if (mapShareRef.current && summaryMarker.countOnly) {
                                    // Deactivate location tracking when summary cluster is clicked
                                    if (isMyLocationActive) {
                                        setIsMyLocationActive(false);
                                        resetMapsMaxBounds();
                                    }
                                    
                                    const calculatedCenter = summaryMarker.getCalculatedCenter();
                                    // Zoom in and center on the calculated mean point of returned places
                                    mapShareRef.current.setView(
                                        new LatLng(calculatedCenter.lat, calculatedCenter.lng),
                                        10
                                    );
                                }
                            });
                        }
                    });
                    summaryClusterMarkersRef.current = newMarkers;
                    console.log(`Created ${newMarkers.length} summary markers for 3x3 grid cells`);
                }
            } else {
                // Zoom >= 10, use full place details
                console.log("High zoom level (>= 10), using full details mode for bounds:", currentBounds);
                
                // Remove summary markers if exist
                if (mapShareRef.current) {
                    console.log(`Removing ${summaryClusterMarkersRef.current.length} summary markers (switching to full details)`);
                    summaryClusterMarkersRef.current.forEach(marker => {
                        if (mapShareRef.current) {
                            mapShareRef.current.removeLayer(marker);
                        }
                    });
                    summaryClusterMarkersRef.current = [];
                }

                // Get full place details
                if (!bounds) {
                    console.log("DEFAULT bounds:", mapShareRef.current?.getBounds())
                    // getPlacesListForBounds is called with 0.0 tolerance to make place markers popup animations visible
                    // When preloaded outside of visible area (with tolerance) animation is not visible
                    await PlaceDataUtils.getPlacesListForBounds(user, mapShareRef.current?.getBounds(), 0.0, places, setPlaces)
                } else {
                    console.log("Getting places for bounds: ", bounds)
                    await PlaceDataUtils.getPlacesListForBounds(user, bounds, 0.0, places, setPlaces)
                }
            }
        } catch (error) {
            console.error("Error from list places: ", error)
            setPlaces(places)
        } finally {
            isUpdatingPlacesRef.current = false;
        }
    };

    useEffect(() => {
        // Do not do anything - not clear if user is auth or unauth yet
        if ((!user && authStatus === 'authenticated') || authStatus === 'configuring') {
            return;
        }

        if (user) {
            const setUserAttrs = async () => {
                try {
                    await fetchUserAttributes()

                    const currentAuthSession = await fetchAuthSession();
                    setAuthSession(currentAuthSession)
                } catch (error) {
                    console.warn("Auth error: ", error);
                    setAuthSession(undefined)
                }

            }

            setUserAttrs()
        } else {
            setAuthSession(undefined)
        }

        // Works for tab change, etc. called at least twice each time
        window.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                // console.info("Page visibility change hidden.")
                setIsMapWindowVisible(false)
            }
            else {
                // console.info("Page visibility change visible.")
                setIsMapWindowVisible(true)
            }
        })

        // Map location scheduler
        const scheduler = setInterval(() => {
            // If map object is available request location refresh
            mapShareRef.current && mapShareRef.current.locate()
        }, 5000)

        mapShareRef.current?.locate()

        return () => {
            // Unsubscribe here
            PlaceDataUtils.unsubsribeAll()
            clearInterval(scheduler)
            mapShareRef.current?.clearAllEventListeners()
            
            // Clear any pending place refresh timeout
            if (placeRefreshTimeoutRef.current) {
                clearTimeout(placeRefreshTimeoutRef.current);
                placeRefreshTimeoutRef.current = undefined;
            }
        }
    }, [user, authStatus]);

    // Separate useEffect to update markers when places list changes
    useEffect(() => {
        updateMarkers()
    }, [places])

    // Handle URL changes - sync URL with overlay state
    useEffect(() => {
        // Skip if this is our own navigation (to avoid loops)
        if (isInternalNavigationRef.current) {
            isInternalNavigationRef.current = false;
            return;
        }

        const pathname = location.pathname;

        if (pathname === '/new') {
            // Open create place overlay
            // If clicking on map to create, newPlaceLatLen will already be set
            // If opening from URL, we need to set a default location or handle differently
            if (!newPlaceLatLen && mapShareRef.current) {
                // Use current map center if opening from URL
                const center = mapShareRef.current.getCenter();
                setNewPlaceLatLen(center);
            }
            setShowCreateNewPlaceOverlay(true);
            document.body.classList.add('overlay-open');
            document.documentElement.classList.add('overlay-open');
            setIsPlaceOverviewVisible(false);
        } else if (placeId) {
            // Open place overview
            isOpeningFromUrlRef.current = true;
            setShowCreateNewPlaceOverlay(false);
            
            // Find place by ID in current places list
            const place = places.find(p => p.placeId === placeId);
            if (place) {
                setSelectedPlaceDefinition(place);
                setIsPlaceOverviewVisible(true);
                document.body.classList.add('overlay-open');
                document.documentElement.classList.add('overlay-open');
                
                // Center map when opening from URL
                if (mapShareRef.current) {
                    const placeLatLng = new LatLng(place.lat, place.lng);
                    mapShareRef.current.setView(placeLatLng, DEFAULT_MAP_ZOOM);
                }
            } else {
                // Place not found in current list - try to load places with expanded bounds
                // and then find the place. This is a fallback for places not in current view
                if (mapShareRef.current) {
                    const currentBounds = mapShareRef.current.getBounds();
                    // Expand bounds significantly to increase chance of finding the place
                    const expandedBounds = currentBounds.pad(2.0); // 200% padding
                    fillAndSubscribePlacesList(expandedBounds);
                } else {
                    // If map not ready, just try default bounds
                    fillAndSubscribePlacesList();
                }
            }
        } else if (pathname === '/user' || pathname === '/user/legal' || pathname === '/user/report') {
            // Open user info overlay (handles user, legal notice, and report routes)
            setShowCreateNewPlaceOverlay(false);
            setIsPlaceOverviewVisible(false);
            setShowUserInfoOverlay(true);
            document.body.classList.add('overlay-open');
            document.documentElement.classList.add('overlay-open');
        } else if (pathname === '/') {
            // Close all overlays
            setShowCreateNewPlaceOverlay(false);
            setIsPlaceOverviewVisible(false);
            setSelectedPlaceDefinition(null);
            setShowUserInfoOverlay(false);
            document.body.classList.remove('overlay-open');
            document.documentElement.classList.remove('overlay-open');
        }
    }, [location.pathname, placeId]);

    // When places are loaded and we're waiting for a specific placeId, open it
    useEffect(() => {
        if (placeId && isOpeningFromUrlRef.current && !selectedPlaceDefinition) {
            const place = places.find(p => p.placeId === placeId);
            if (place) {
                setSelectedPlaceDefinition(place);
                setIsPlaceOverviewVisible(true);
                document.body.classList.add('overlay-open');
                document.documentElement.classList.add('overlay-open');
                
                // Center map when opening from URL
                if (mapShareRef.current) {
                    const placeLatLng = new LatLng(place.lat, place.lng);
                    mapShareRef.current.setView(placeLatLng, DEFAULT_MAP_ZOOM);
                }
            }
        }
    }, [places, placeId, selectedPlaceDefinition]);

    // Markers are added programatically to map as layers. This is needed for dymanic adding/removing markers
    // Updated list of places is compared to existing marker layers in map. This prevents remove all/add all behavior.
    // Keep markers in view in map is crutial for markers fadeIn animation to work as expected.
    const updateMarkers = () => {
        // Early return if map is not yet initialized to prevent race condition
        if (!mapShareRef.current) {
            console.debug("Map not yet initialized, skipping marker update")
            return
        }

        // Skip marker update if in summary mode (zoom < 10)
        // Summary mode uses SummaryClusterMarker instead of individual place markers
        const currentZoom = mapShareRef.current.getZoom();
        if (currentZoom < 10) {
            console.debug("Zoom < 10, skipping individual marker update (using summary mode)")
            return
        }

        // Initialize cluster group if not exists
        if (!markerClusterGroupRef.current) {
            markerClusterGroupRef.current = new PlaceMarkerClusterGroup(() => {
                // Deactivate location tracking when cluster is clicked
                // This prevents the map from jumping back to device location after cluster zoom
                setIsMyLocationActive(false);
                resetMapsMaxBounds();
            })
            markerClusterGroupRef.current.addTo(mapShareRef.current)
        }

        console.debug("Updating markers, places count: ", places.length)
        let placesToAdd = [...places]

        let removedMarkersCount = 0
        // Get all place markers from the cluster group
        const existingMarkers = markerClusterGroupRef.current.getAllPlaceMarkers()
        
        existingMarkers.forEach((marker) => {
            const existingLayerPlaceId = marker.getPlaceId()

            // Remove all places with already existing markers from placesToAdd
            placesToAdd = placesToAdd.filter(p => p.placeId !== existingLayerPlaceId)

            // If existing marker layer's placeId is not in current places, remove it
            if (!places.some(p => p.placeId === existingLayerPlaceId)) {
                console.debug("Removing layer: ", marker.getPlaceId())
                markerClusterGroupRef.current?.removeLayer(marker)
                removedMarkersCount++
            }
        })

        console.debug("Removed markers count: ", removedMarkersCount)
        console.debug("Adding places[count: ", placesToAdd.length, "]: ", placesToAdd)

        placesToAdd.forEach(p => {
            // Double-check marker doesn't already exist to prevent duplicates
            if (markerClusterGroupRef.current?.hasMarkerWithPlaceId(p.placeId)) {
                console.debug("Marker already exists for place: ", p.placeId, " - skipping")
                return
            }

            let markerClass 
            console.debug("Adding place for: ", p.placeId)
            if (!ClientDeviceUtils.isTouchControlUsed()) {
                markerClass = new PlaceMarkerHoverClass(p, user, handlePlaceClick)
            } else {
                markerClass = new PlaceMarkerNoHoverClass(p, user, handlePlaceClick, (isExpanded: boolean) => {
                    console.info("isExpanded: ", isExpanded)
                    expandedPlacesMapRef.current[p.placeId] = isExpanded;
                }, mapShareRef.current as L.Map)
            }
            markerClusterGroupRef.current?.addLayer(markerClass)
        })

        if (removedMarkersCount > 0 || placesToAdd.length > 0) {
            // This call triggers render for map, without need to refresh whole page - enables dymanic adding/removing markers
            mapShareRef.current.invalidateSize()
        }
    }

    const handleNewItemClick = () => {
        isInternalNavigationRef.current = true;
        navigate('/new');
        setShowCreateNewPlaceOverlay(true);  // Show the overlay when the 'New Item' button is clicked
        // Add overlay-open class to body and html to prevent scrolling
        document.body.classList.add('overlay-open');
        document.documentElement.classList.add('overlay-open');
    };

    const handleCloseOverlay = () => {
        isInternalNavigationRef.current = true;
        navigate('/');
        setShowCreateNewPlaceOverlay(false); // Hide the overlay
        // Remove overlay-open class from body and html
        document.body.classList.remove('overlay-open');
        document.documentElement.classList.remove('overlay-open');
    };

    const handleUShowUserOverlay = () => {
        isInternalNavigationRef.current = true;
        navigate('/user');
        setShowUserInfoOverlay(true);  // Show the overlay when the 'New Item' button is clicked
        // Add overlay-open class to body and html to prevent scrolling
        document.body.classList.add('overlay-open');
        document.documentElement.classList.add('overlay-open');
    };

    const handleCloseUserInfoOverlay = () => {
        isInternalNavigationRef.current = true;
        navigate('/');
        setShowUserInfoOverlay(false); // Hide the overlay
        // Remove overlay-open class from body and html
        document.body.classList.remove('overlay-open');
        document.documentElement.classList.remove('overlay-open');
        markerClusterGroupRef.current?.clearLayers();
        updateMarkers();
    };

    // TODO is callback needed? What about calling 'createNewPlace()' right from 'CreateNewPlacePopup'?
    const handleNewPlaceCreation = (newPlace: NewPlaceDefinition) => {
        PlaceDataUtils.createNewPlace(newPlace, I18nTextUtils.getCurrentLanguageDetail())

        handleCloseOverlay()

        // Wait for backend to process the new place, then refresh the map
        // This gives the user feedback that the place was created
        const PLACE_CREATION_REFRESH_DELAY_MS = 3000; // 3 seconds
        
        console.info("Scheduling place refresh after creation in", PLACE_CREATION_REFRESH_DELAY_MS, "ms");
        placeRefreshTimeoutRef.current = setTimeout(() => {
            console.info("Refreshing places after new place creation");
            
            // Expand bounds to include the newly created place
            // This ensures the new place appears on the map even if it's outside current view
            if (mapShareRef.current) {
                const currentBounds = mapShareRef.current.getBounds();
                const newPlaceLatLng = new LatLng(newPlace.lat, newPlace.lng);
                
                // Create expanded bounds that include both current view and new place
                const expandedBounds = currentBounds.extend(newPlaceLatLng);
                
                console.info("Using expanded bounds to include new place at", newPlaceLatLng.toString());
                fillAndSubscribePlacesList(expandedBounds);
            } else {
                // Fallback to default bounds if map is not available
                fillAndSubscribePlacesList();
            }
            
            placeRefreshTimeoutRef.current = undefined;
        }, PLACE_CREATION_REFRESH_DELAY_MS);
    };

    const removePlace = (event: { stopPropagation: () => void; }, placeToDelete: PlaceDefinitionMapMember) => {
        event.stopPropagation()
        setNewMarkerPositions(newMarkerPositions.filter((p: PlaceDefinitionMapMember) => p.key !== placeToDelete.key))
    }

    const OpenOverlay = () => {
        useMapEvents({
            click(e) {
                // Skip if the event was marked to be ignored
                if ((e as any).skipPlaceOpenDueToPreviouslyExpandedPlace) {
                    return;
                }
                
                // Only handle click if no marker is expanded
                if (!Object.values(expandedPlacesMapRef.current).some(expanded => expanded)) {
                    setNewPlaceLatLen(e.latlng);
                    handleNewItemClick();
                }
            },
        });

        return (<></>);
    };

    const handlePlaceClick = (placeDefinition: PlaceDefinition, shouldCenterMap: boolean = false) => {
        isInternalNavigationRef.current = true;
        isOpeningFromUrlRef.current = false; // Opening from map click
        navigate(`/place/${placeDefinition.placeId}`);
        setSelectedPlaceDefinition(placeDefinition)
        setIsPlaceOverviewVisible(true)
        // Add overlay-open class to body and html to prevent scrolling
        document.body.classList.add('overlay-open');
        document.documentElement.classList.add('overlay-open');
        
        // Center map only if explicitly requested (when opening from URL)
        if (shouldCenterMap && mapShareRef.current && placeDefinition) {
            const placeLatLng = new LatLng(placeDefinition.lat, placeDefinition.lng);
            mapShareRef.current.setView(placeLatLng, DEFAULT_MAP_ZOOM);
        }
    }

    const updateIsMyLocationActive = (newValue: boolean): undefined => {
        console.info("Update state of loc active to ", newValue)
        if (!newValue) {
            resetMapsMaxBounds()
            shouldZoomToDefaultOnLocationFoundRef.current = false
        } else {
            // When activating location, check if we need to zoom to level 10
            if (mapShareRef.current) {
                const currentZoom = mapShareRef.current.getZoom()
                if (currentZoom < DEFAULT_MAP_ZOOM) {
                    if (myLocation) {
                        // Location is already available, zoom immediately
                        resetMapsMaxBounds()
                        mapShareRef.current.flyTo(myLocation, DEFAULT_MAP_ZOOM)
                    } else {
                        // Location not available yet, set flag to zoom when location is found
                        shouldZoomToDefaultOnLocationFoundRef.current = true
                    }
                }
            }
        }
        myLocation && setIsMyLocationActive(newValue)
    }

    const resetMapsMaxBounds = () => {
        // Reset bounds when location gets inactive
        // TODO: null was used before - test if new way works
        // mapShareRef.current && mapShareRef.current.setMaxBounds(null)
        if (mapShareRef.current) {
            mapShareRef.current.setMaxBounds(new LatLngBounds([-90, -180], [90, 180]))
        }
    }

    const updateMapBounds = (map: Leaflet.Map) => {
        if (!map || !map.getBounds()) {
            console.warn("No map or bounds or currentMapPlacesBounds map: ", map);
            return
        }

        const newBounds = map.getBounds()

        fillAndSubscribePlacesList(newBounds)
    }

    const LocateMap = () => {
        const map = useMapEvents({

            locationfound(e) {
                // console.warn("Location found!");
                positionLoading = false
                if (!myLocation?.equals(e.latlng)) {
                    setMyLocation(e.latlng)
                    // If we need to zoom to default when location is found, do it now
                    if (shouldZoomToDefaultOnLocationFoundRef.current && mapShareRef.current) {
                        shouldZoomToDefaultOnLocationFoundRef.current = false
                        resetMapsMaxBounds()
                        mapShareRef.current.flyTo(e.latlng, DEFAULT_MAP_ZOOM)
                    }
                }
            },
            locationerror(e) {
                positionLoading = false
                setIsMyLocationActive(false)
                setMyLocation(undefined)
                resetMapsMaxBounds()
                console.warn("Location Error!");

                // Hack move init position slightly for failed location during init, so fly will we used if location start working later in session and locationActive=true
                if (map.getCenter().equals(INITIAL_MAP_POSITION) && map.getZoom() == INITIAL_MAP_ZOOM) {
                    map.flyTo([map.getCenter().lat, map.getCenter().lng + 0.001], INITIAL_MAP_ZOOM)
                }
                // In case location fails initially and later start work, use fly
            },

            moveend(e) {
                // TODO review this - in mobile view when location is active this is called repeatedly
                console.log("moveend: ")
                
                // Cancel any pending place refresh since map movement will trigger its own refresh
                if (placeRefreshTimeoutRef.current) {
                    console.info("Canceling scheduled place refresh due to map movement");
                    clearTimeout(placeRefreshTimeoutRef.current);
                    placeRefreshTimeoutRef.current = undefined;
                }
                
                if (isMyLocationActive && myLocation) {
                    // If isMyLocationActive and moveend event - moved due to location change
                    map.setMaxBounds(new LatLngBounds(myLocation, myLocation))
                }

                setTimeout(() => {
                    updateMapBounds(map)
                }, LOCATION_UPDATE_TIMEOUT_MS)
            },
            zoomend(e) {
                // Log zoom value when zoom changes
                const newZoom = map.getZoom();
                console.log("Map zoom changed to:", newZoom);
                
                // Refresh places data when zoom crosses threshold (10)
                // This switches between summary mode (zoom < 10) and full details mode (zoom >= 10)
                setTimeout(() => {
                    updateMapBounds(map)
                }, LOCATION_UPDATE_TIMEOUT_MS)
                
                // If isMyLocationActive and zoomend event - moved due to location change
                if (isMyLocationActive && myLocation) {
                    map.setMaxBounds(new LatLngBounds(myLocation, myLocation))
                }
            },
            move(e) {
                if (e.hasOwnProperty('originalEvent')) {
                    // Map moved by user
                    isMyLocationActive && setIsMyLocationActive(false)
                    isMyLocationActive && resetMapsMaxBounds()
                }
            },

        })

        mapShareRef.current = map

        // When position changes, LocateMap component gets reinitialized - therefore this code can be used for myLocation update:
        if (isMyLocationActive && myLocation) {
            if (map.getCenter().equals(INITIAL_MAP_POSITION) && map.getZoom() == INITIAL_MAP_ZOOM) {
                // Looks like initial location change - do not fly, jump using setView. Map looks like shown on location on init.
                // Reset bounds to allow map move
                resetMapsMaxBounds()
                map.setView(myLocation, DEFAULT_MAP_ZOOM)
            } else {
                // Reset bounds to allow map move
                // Used theshold to avoid loop when locked on location
                if (map.getCenter().distanceTo(myLocation) > FLY_TO_LOCATION_DISTANCE_METERS_TRESHOLD) {
                    resetMapsMaxBounds()
                    // Check if we should zoom to default (when location was just activated at zoom < 10)
                    if (shouldZoomToDefaultOnLocationFoundRef.current) {
                        shouldZoomToDefaultOnLocationFoundRef.current = false
                        map.flyTo(myLocation, DEFAULT_MAP_ZOOM)
                    } else {
                        // Preserve user's zoom level - only use DEFAULT_MAP_ZOOM if current zoom is >= 10
                        // This allows users to zoom out below 10 and keep that zoom level
                        const currentZoom = map.getZoom()
                        const targetZoom = currentZoom >= DEFAULT_MAP_ZOOM ? DEFAULT_MAP_ZOOM : currentZoom
                        map.flyTo(myLocation, targetZoom)
                    }
                }
            }
        }

        return <></>
    }

    const PointerEventHandler = () => {
        useMapEvents({
            mousedown(e) {
                if (e.originalEvent) {
                    ClientDeviceUtils.handlePointerDown(e.originalEvent as PointerEvent);
                }
            },
        });

        return <></>;
    };

    const showSearchedLocation = (locationSearchResult: SearchResult<RawResult>) => {
        if (locationSearchResult.bounds) {
            const bounds1: LatLng = new LatLng(locationSearchResult.bounds?.[0][0], locationSearchResult.bounds?.[0][1])
            const bounds2: LatLng = new LatLng(locationSearchResult.bounds?.[1][0], locationSearchResult.bounds?.[1][1])

            const targetBounds = new LatLngBounds(bounds1, bounds2)

            // Ensure my location is not active, reset bounds to avoid jump back
            setIsMyLocationActive(false)
            resetMapsMaxBounds()

            mapShareRef.current?.flyToBounds(targetBounds)
        }

    }

    return (
        <>
            {/* This script does not do much - but is ment to be used to make map work... */}
            {/* <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"
                integrity="sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo="
                crossOrigin=""></script> */}

            {/* Attribution control="" or {false} removes all attribution */}
            <MapContainer
                className='map-container-root-zoom-safearea'
                center={INITIAL_MAP_POSITION}
                zoom={INITIAL_MAP_ZOOM}
                scrollWheelZoom={true}
                attributionControl={false}
                zoomControl={false}
            >
                <ZoomControl
                    position='bottomright'
                />
                <LocateMap />
                <PointerEventHandler />
                <MyLocationMarker
                    myLocation={myLocation}
                />
                {/* Suspense needed because page loads before i18n... */}
                {/* <Suspense>
                </Suspense> */}
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <OpenOverlay />
            </MapContainer>

            {/* Header row  */}
            <Flex
                className='map-header-row-flex'
                direction='row-reverse'
                wrap="wrap"
            // gap="0"
            >
                <MapUserIcon
                    onClick={handleUShowUserOverlay}
                />
                <LocationSearch
                    isMapWindowVisible={isMapWindowVisible}
                    onSearchLocationSelected={showSearchedLocation}
                />
            </Flex>

            {/* Overlay is conditionally rendered */}
            {showCreateNewPlaceOverlay && newPlaceLatLen && (
                <div className="overlay">
                    <CreateNewPlacePopup
                        user={user}
                        initialPlaceLatLen={newPlaceLatLen}
                        handleCloseOverlay={handleCloseOverlay}
                        handleNewPlaceCreation={handleNewPlaceCreation}
                        handleShowLogin={() => {
                            setShowCreateNewPlaceOverlay(false);
                            // Remove overlay-open class from body and html
                            document.body.classList.remove('overlay-open');
                            document.documentElement.classList.remove('overlay-open');
                            // Show user info overlay
                            handleUShowUserOverlay();
                        }}
                        authSession={authSession}
                    />
                </div>
            )}

            {showUserInfoOverlay && (
                <div className="overlay">
                    <UserInfoPopup
                        handleCloseOverlay={handleCloseUserInfoOverlay}
                    />
                </div>
            )}

            {isPlaceOverviewVisible && selectedPlaceDefinition && (
                <div className="overlay">
                    <PlaceOverviewPopup
                        placeDefinition={selectedPlaceDefinition}
                        loggedInUser={user}
                        handleCloseOverlay={() => {
                            isInternalNavigationRef.current = true;
                            navigate('/');
                            setIsPlaceOverviewVisible(false);
                            // Remove overlay-open class from body and html
                            document.body.classList.remove('overlay-open');
                            document.documentElement.classList.remove('overlay-open');
                        }}
                        handleShowLogin={() => {
                            setIsPlaceOverviewVisible(false);
                            // Remove overlay-open class from body and html
                            document.body.classList.remove('overlay-open');
                            document.documentElement.classList.remove('overlay-open');
                            // Show user info overlay
                            handleUShowUserOverlay();
                        }}
                    />
                </div>
            )}


            <LocationControls
                updateLocationActive={updateIsMyLocationActive}
                myLocation={myLocation}
                isActive={isMyLocationActive}
                isPositionLoading={positionLoading}
            />
        </>
    );
}


